import { appSurfaces as S, workOs as W } from "@secretaa/contracts-data";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { ReactNode } from "react";
import { FormError } from "../../shared/form.tsx";

/** Local menu contains version references only. Following a link uses the ordinary authenticated
 * manifest loader; navigation does not preload records or bypass the target's current ACL. */
export function SurfaceLocalNavigation({ publication, currentRef }: { publication: S.SurfacePublication; currentRef: W.Ref }) {
  const { t, i18n } = useTranslation();
  const parsed = S.SurfacePublication.safeParse(publication), selected = W.Ref.safeParse(currentRef);
  if (!parsed.success || !selected.success || !parsed.data.surfaceVersionRefs.some(ref => W.sameRef(ref, selected.data)))
    return <FormError message={t("surfaces.notFound")} />;
  const p = parsed.data, language = (i18n.resolvedLanguage ?? i18n.language).startsWith("vi") ? "vi" : "en";
  const path = `/w/${p.scope.workspaceId}${p.scope.projectId ? `/projects/${p.scope.projectId}` : ""}/surfaces/${p.bindingId}`;
  const selectedEntry = p.navigation.entries.find(entry => W.sameRef(entry.surfaceRef, selected.data))!.id;
  const children = new Map<string | null, typeof p.navigation.entries>();
  for (const entry of p.navigation.entries) {
    const siblings = children.get(entry.parentId) ?? []; siblings.push(entry); children.set(entry.parentId, siblings);
  }
  function entries(parentId: string | null): ReactNode {
    const nodes = children.get(parentId);
    if (!nodes) return null;
    return <ul className={parentId === null ? "flex flex-wrap gap-3" : "ml-3 space-y-2 border-l pl-3"}>
      {nodes.map(entry => <li key={entry.id} className="min-w-0 break-words">
        {entry.id === selectedEntry ? <span aria-current="page" className="font-medium">{entry.label[language]}</span> :
          <Link className="text-(--saa-accent) underline" to={`${path}/versions/${entry.surfaceRef.id}/${entry.surfaceRef.version}`}>{entry.label[language]}</Link>}
        {entries(entry.id)}
      </li>)}
    </ul>;
  }
  return <nav aria-label={t("surfaces.published.navigation")}>{entries(null)}</nav>;
}
